import { Fragment, useRef, useState } from 'react';
import { Callout, Container, Heading, Paragraph, Switch } from '@treams/treams-ui';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import * as graphql from 'generated/graphql';
import moment from 'moment';
import { Box, Flex } from 'reflexbox';
import _ from 'lodash';

import {
	ConversationsTableHeader,
	ConversationsTableMomentRow,
	ConversationsTableRow,
} from '../conversations/ConversationsTable';

type DateSection = {
	before?: moment.Moment;
	after?: moment.Moment;
	label: string;
	isDue?: boolean;
	isCompleted?: boolean;
	isToday?: boolean;
};

function findNearestSection(
	evaluationSubmission: graphql.EvaluationSubmission | graphql.MomentInstance,
	sections: DateSection[],
): DateSection {
	if (
		evaluationSubmission.__typename === 'EvaluationSubmission' &&
		evaluationSubmission.manager.isCompleted
	) {
		return sections[0];
	}

	const deadline =
		evaluationSubmission.__typename === 'EvaluationSubmission'
			? evaluationSubmission.deadline
			: evaluationSubmission.__typename === 'MomentInstance'
			? evaluationSubmission.endDate
			: undefined;

	return sections.slice(1, sections.length - 1).reduce(
		(result: DateSection, section: DateSection) => {
			if (moment(deadline).isBetween(section.after, section.before, undefined, '[]')) {
				return section;
			}
			return result;
		},
		sections[sections.length - 1],
	);
}

function dateSections(t: TFunction) {
	const now = moment();
	const thisWeek = moment(now).endOf('day');
	const nextWeek = moment(thisWeek).add(1, 'week');
	const later = moment(nextWeek).add(1, 'week');

	return [
		{
			isCompleted: true,
			label: t('manager.conversations.forecast.section.completed'),
		},
		{
			after: moment('20000101', 'YYYYMMDD'),
			before: moment(now).subtract(1, 'days').endOf('day'),
			isDue: true,
			label: t('manager.conversations.forecast.section.due'),
		},
		{
			after: moment(now).startOf('day'),
			before: moment(now).endOf('day'),
			isToday: true,
			label: t('manager.conversations.forecast.section.today'),
		},
		{
			// Explicitly not startOf('week') as we only want to return dates after today
			after: moment(thisWeek),
			before: moment(thisWeek).endOf('week'),
			label: t('manager.conversations.forecast.section.thisWeek', {
				startDate: moment(thisWeek).startOf('week').format('LL'),
				endDate: moment(thisWeek).endOf('week').format('LL'),
			}),
		},
		{
			after: moment(nextWeek).startOf('week'),
			before: moment(nextWeek).endOf('week'),
			label: t('manager.conversations.forecast.section.nextWeek', {
				startDate: moment(nextWeek).startOf('week').format('LL'),
				endDate: moment(nextWeek).endOf('week').format('LL'),
			}),
		},
		{
			after: moment(later).startOf('week'),
			before: moment(later).add(3, 'years'),
			label: t('manager.conversations.forecast.section.later'),
		},
	];
}

function groupedByDate(
	items: (graphql.EvaluationSubmission | graphql.MomentInstance)[],
	sections: DateSection[],
): Record<string, (graphql.EvaluationSubmission | graphql.MomentInstance)[]> {
	return _.groupBy(
		items,
		(evaluationSubmission) => findNearestSection(evaluationSubmission, sections).label,
	);
}

export default function ManagerCalendar() {
	const after = useRef(moment().subtract(3, 'months')).current;

	const { t } = useTranslation();
	const forecast = graphql.useEvaluationSubmissionsFutureForecastQuery({
		variables: {
			isArchived: false,
			after,
		},
	});
	const [archive] = graphql.useArchiveEvaluationSubmissionMutation();
	const [skip] = graphql.useSkipMomentInstanceMutation();
	const [complete] = graphql.useCompleteConversationMutation();
	const [uncomplete] = graphql.useUncompleteConversationMutation();

	const [showCompleted, setShowCompleted] = useState(false);

	const sections = dateSections(t);

	let submissions = forecast.data
		? _.concat(
				forecast.data?.viewer.outgoingEvaluationSubmissions.edges
					.map(({ node }) => node as graphql.EvaluationSubmission)
					.filter((submission) => !submission.isArchived) as (
					| graphql.EvaluationSubmission
					| graphql.MomentInstance
				)[],
				forecast.data?.viewer.managees.flatMap((managee) =>
					managee.moments.map((moment) => ({ ...moment, user: managee }) as graphql.MomentInstance),
				),
		  )
		: [];

	submissions = _.uniqBy(submissions, (submission) => submission.id);

	submissions = _.sortBy(submissions, (submission) =>
		'deadline' in submission ? submission.deadline : submission.endDate,
	);

	const groupedSubmissions = forecast.data ? groupedByDate(submissions, sections) : {};

	return (
		<Box my={4}>
			<Container>
				<Heading>{t('manager.conversations.forecast.title')}</Heading>
				<Paragraph>{t('manager.conversations.forecast.description')}</Paragraph>

				{sections.find((section) => section.isCompleted && groupedSubmissions[section.label]) && (
					<Flex alignItems="center" mt={3} onClick={() => setShowCompleted((value) => !value)}>
						<Box flexGrow={0} flexShrink={0} mr={2}>
							<Switch on={showCompleted} onChange={() => {}} />
						</Box>
						<Paragraph flexGrow={1} flexShrink={1}>
							{t('manager.conversations.forecast.showCompleted')}
						</Paragraph>
					</Flex>
				)}

				<Callout color="employee" mt={3}>
					{t('manager.conversations.forecast.markAsCompleted.explanation')}
				</Callout>

				{sections
					.filter((section) => groupedSubmissions[section.label] !== undefined || section.isToday)
					.filter((section) => !section.isCompleted || showCompleted)
					.map((section, index) => (
						<Fragment key={index}>
							<ConversationsTableHeader
								title={section.label}
								index={index}
								isDue={section.isDue}
								isCompleted={section.isCompleted}
								count={groupedSubmissions[section.label]?.length ?? 0}
							/>
							{!groupedSubmissions[section.label] && (
								<Paragraph py={2}>{t('manager.conversations.forecast.empty.today')}</Paragraph>
							)}
							{(groupedSubmissions[section.label] ?? []).map((evaluationSubmission) =>
								evaluationSubmission.__typename === 'EvaluationSubmission' ? (
									<ConversationsTableRow
										key={evaluationSubmission.id}
										evaluationSubmission={evaluationSubmission}
										onArchive={async ({ id }) => {
											await archive({ variables: { input: { id } } });
										}}
										onComplete={async ({ id }) => {
											await complete({ variables: { input: { id } } });
											setShowCompleted(true);
										}}
										onUncomplete={async ({ id }) => {
											await uncomplete({ variables: { input: { id } } });
										}}
									/>
								) : (
									<ConversationsTableMomentRow
										key={evaluationSubmission.id}
										momentInstance={evaluationSubmission as graphql.MomentInstance}
										onSkip={async ({ id }) => {
											await skip({
												variables: {
													input: {
														id,
														skipReason: graphql.MomentSkipReason.Other,
														skipReasonOther: '-',
													},
												},
											});
										}}
									/>
								),
							)}
						</Fragment>
					))}
			</Container>
		</Box>
	);
}
